/** @jsxImportSource @emotion/react */
import { Popover, Portal } from '@ark-ui/react';
import { css } from '@emotion/react';
import { useTranslation } from '@vitvio/locales';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import type React from 'react';
import { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { zIndex } from '../constants/zIndex';
import { borderWidth, transition, shadow } from '../constants/style';
import type { IconName } from '../icons';
import { useTheme } from '../provider/useTheme';
import { CoreIcon } from './CoreIcon';
import type { TranslationValue } from './translation';

dayjs.extend(customParseFormat);

/** Canonical display/storage format for committed time text. */
const TIME_FORMAT = 'HH:mm';
/** Compact typed format accepted on commit (`HHmm`). */
const COMPACT_TIME_FORMAT = 'HHmm';

/**
 * Allowed minute step intervals for time selection
 */
export type MinuteStep = 1 | 5 | 10 | 15 | 30 | 60;
/** Accepts a direct Dayjs bound or a lazy getter returning a Dayjs bound. */
export type TimeSelectBound = dayjs.Dayjs | (() => dayjs.Dayjs | null) | null;

/**
 * Props for the TimeSelect component
 */
export interface TimeSelectProps {
  /** Currently selected time value; also primary reference date for typed-time normalization. */
  value?: dayjs.Dayjs | null;
  /** Called whenever the committed time changes (typed or selected). */
  onChange?: (value: dayjs.Dayjs | null) => void;
  /** Plain placeholder text; used only when `placeholderPath` is not set. */
  placeholder?: string;
  /** Translation key for placeholder text (takes precedence over `placeholder`). */
  placeholderPath?: string;
  /** Optional interpolation values used with `placeholderPath`. */
  placeholderValue?: TranslationValue;
  /** Interval between selectable options in minutes. */
  minuteStep?: MinuteStep;
  /**
   * Minimum allowed time (inclusive).
   * Can be a Dayjs value or lazy getter. If passing a getter, memoize it to avoid unnecessary rerenders.
   */
  min?: TimeSelectBound;
  /**
   * Maximum allowed time (inclusive).
   * Can be a Dayjs value or lazy getter. If passing a getter, memoize it to avoid unnecessary rerenders.
   */
  max?: TimeSelectBound;
  /** Whether the input is disabled. Typed input clamps on `value ?? min ?? max ?? dayjs()`. */
  isDisabled?: boolean;
  /** Whether error styling is shown. */
  isInvalid?: boolean;
  /** Optional leading icon token. */
  icon?: IconName;
  /** Whether to focus the input on mount. */
  autoFocus?: boolean;
}

/** Selects the reference date used to anchor time-only parsing/generation/comparison. */
const getAnchorDate = (
  referenceDate: dayjs.Dayjs | null = null,
  min: dayjs.Dayjs | null = null,
  max: dayjs.Dayjs | null = null,
): dayjs.Dayjs => (referenceDate ?? min ?? max ?? dayjs()).second(0).millisecond(0);

/** Projects the source time-of-day onto the provided anchor date. */
const anchorTimeOfDay = (source: dayjs.Dayjs, anchorDate: dayjs.Dayjs): dayjs.Dayjs =>
  anchorDate.hour(source.hour()).minute(source.minute()).second(0).millisecond(0);

type NormalizeTimeInputResult =
  | { kind: 'empty' }
  | { kind: 'invalid' }
  | { kind: 'value'; date: dayjs.Dayjs; text: string };

/** Parses/validates typed input and returns normalized empty/invalid/value result for commit handling. */
const normalizeTimeInput = ({
  rawValue,
  anchorDate,
  min,
  max,
}: {
  rawValue: string;
  anchorDate: dayjs.Dayjs;
  min: dayjs.Dayjs | null;
  max: dayjs.Dayjs | null;
}): NormalizeTimeInputResult => {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return { kind: 'empty' };
  }

  const parsed = dayjs(trimmed, [TIME_FORMAT, COMPACT_TIME_FORMAT], true);
  if (!parsed.isValid()) {
    return { kind: 'invalid' };
  }

  const minTime = min ? anchorTimeOfDay(min, anchorDate) : null;
  const maxTime = max ? anchorTimeOfDay(max, anchorDate) : null;
  let next = anchorDate.hour(parsed.hour()).minute(parsed.minute()).second(0).millisecond(0);

  if (minTime && next.isBefore(minTime, 'minute')) {
    next = minTime;
  } else if (maxTime && next.isAfter(maxTime, 'minute')) {
    next = maxTime;
  }

  return {
    kind: 'value',
    date: next,
    text: next.format(TIME_FORMAT),
  };
};

/**
 * Generates an array of time options based on the provided step and boundaries
 */
const generateTimes = (
  step = 15,
  min: dayjs.Dayjs | null = null,
  max: dayjs.Dayjs | null = null,
  referenceDate: dayjs.Dayjs | null = null,
): dayjs.Dayjs[] => {
  const validStep = Math.max(1, step);
  const anchorDate = getAnchorDate(referenceDate, min, max);
  const startOfDay = anchorDate.startOf('day');
  const endOfDay = anchorDate.endOf('day').startOf('minute');
  const startTime = (min ? anchorTimeOfDay(min, anchorDate) : startOfDay).startOf('minute');
  const endTime = (max ? anchorTimeOfDay(max, anchorDate) : endOfDay).startOf('minute');

  if (startTime.isAfter(endTime, 'minute')) {
    return [];
  }

  const generated: dayjs.Dayjs[] = [];
  for (let current = startTime; !current.isAfter(endTime, 'minute'); current = current.add(validStep, 'minute')) {
    generated.push(current);
  }
  return generated;
};

/** Finds the closest option index to a target time for initial open scroll targeting. */
const getClosestTimeIndex = (times: readonly dayjs.Dayjs[], target: dayjs.Dayjs): number | null => {
  if (times.length === 0) {
    return null;
  }

  let closestIndex = 0;
  let closestDiff = Math.abs(times[0].diff(target, 'minute'));
  for (let index = 1; index < times.length; index += 1) {
    const diff = Math.abs(times[index].diff(target, 'minute'));
    if (diff < closestDiff) {
      closestIndex = index;
      closestDiff = diff;
    }
  }
  return closestIndex;
};

/** Converts a Dayjs time to minutes since midnight for stable time-of-day ordering. */
const toMinutesOfDay = (value: dayjs.Dayjs): number => value.hour() * 60 + value.minute();

/**
 * Sanitizes in-progress input so users can type/paste naturally while keeping a lean shape:
 * digits and literal colons only, with at most 4 digits total (`HHmm` budget).
 */
const sanitizeTimeDraft = (value: string): string => {
  let digits = 0;
  let sanitized = '';

  for (const char of value) {
    if (char >= '0' && char <= '9') {
      if (digits >= 4) {
        continue;
      }
      digits += 1;
      sanitized += char;
      continue;
    }

    if (char === ':') {
      sanitized += char;
    }
  }

  return sanitized;
};

/**
 * Inserts a committed value into an already-sorted option list when it is missing.
 *
 * The insert is minute-unique and keeps ascending time-of-day ordering.
 * When explicit min/max bounds are provided, out-of-range committed values are ignored.
 * Returns the original list when no insertion is needed.
 */
const insertSortedUniqueTime = (
  options: readonly dayjs.Dayjs[],
  committed: dayjs.Dayjs | null,
  min: dayjs.Dayjs | null,
  max: dayjs.Dayjs | null,
): readonly dayjs.Dayjs[] => {
  if (!committed || options.length === 0) {
    return options;
  }

  const committedMinutes = toMinutesOfDay(committed);
  if (min && committedMinutes < toMinutesOfDay(min)) {
    return options;
  }
  if (max && committedMinutes > toMinutesOfDay(max)) {
    return options;
  }

  if (options.some((option) => option.isSame(committed, 'minute'))) {
    return options;
  }

  const insertIndex = options.findIndex((option) => toMinutesOfDay(option) > committedMinutes);
  if (insertIndex === -1) {
    return [...options, committed];
  }

  return [...options.slice(0, insertIndex), committed, ...options.slice(insertIndex)];
};

type ActiveNavigationDirection = 'up' | 'down';

/**
 * Computes the next active option index for keyboard navigation.
 *
 * Rules:
 * - If list is empty, returns null.
 * - When open with an existing active index, moves one step and wraps cyclically.
 * - Otherwise, seeds from committed value using strict neighbour semantics:
 *   down -> first option strictly greater (or wrap to first),
 *   up -> last option strictly smaller (or wrap to last).
 * - If no committed value exists, seeds to first/last depending on direction.
 */
const getNextActiveIndex = ({
  direction,
  isOpen,
  currentActiveIndex,
  committedValue,
  options,
}: {
  direction: ActiveNavigationDirection;
  isOpen: boolean;
  currentActiveIndex: number | null;
  committedValue: dayjs.Dayjs | null;
  options: readonly dayjs.Dayjs[];
}): number | null => {
  if (options.length === 0) {
    return null;
  }

  if (isOpen && currentActiveIndex !== null) {
    if (direction === 'down') {
      return (currentActiveIndex + 1) % options.length;
    }
    return (currentActiveIndex - 1 + options.length) % options.length;
  }

  if (!committedValue) {
    return direction === 'down' ? 0 : options.length - 1;
  }

  const committedMinutes = toMinutesOfDay(committedValue);
  if (direction === 'down') {
    const nextIndex = options.findIndex((option) => toMinutesOfDay(option) > committedMinutes);
    return nextIndex >= 0 ? nextIndex : 0;
  }

  for (let index = options.length - 1; index >= 0; index -= 1) {
    if (toMinutesOfDay(options[index]) < committedMinutes) {
      return index;
    }
  }
  return options.length - 1;
};

/** Resolves a direct or lazy min/max bound prop into a Dayjs value. */
const resolveTimeBound = (bound: TimeSelectBound | undefined): dayjs.Dayjs | null => {
  if (!bound) {
    return null;
  }
  return typeof bound === 'function' ? bound() : bound;
};

/**
 * Time picker component with text input and popover list assist.
 * Commit/revert behavior is centralized in `commitInputValue` to keep key/blur flows deterministic.
 *
 * **Memoization note**: `TimeSelect` is wrapped in `memo()`. For the shallow-equality check to
 * be effective, `value`, `min`, and `max` Dayjs props should be stable references (e.g. created
 * outside render or memoized with `useMemo`). Passing inline `dayjs()` calls directly will
 * cause re-renders on every parent render.
 */
export const TimeSelect: React.FC<TimeSelectProps> = memo(
  ({
    value,
    onChange,
    placeholder,
    placeholderPath,
    placeholderValue,
    minuteStep = 15,
    min: minProp = null,
    max: maxProp = null,
    isDisabled = false,
    isInvalid = false,
    icon = 'schedule',
    autoFocus = false,
  }) => {
    const { t } = useTranslation();
    const trimmedPlaceholderPath = placeholderPath?.trim();
    const placeholderText = trimmedPlaceholderPath
      ? t(trimmedPlaceholderPath, placeholderValue)
      : (placeholder ?? t('common:time.selectTime'));

    const theme = useTheme();
    const { getColor, getSpacing, getRadius, getFont } = theme;
    const listboxId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const interactionModeRef = useRef<'keyboard' | 'pointer'>('pointer');
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value?.format(TIME_FORMAT) || '');
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    // Evaluate lazy bounds on every render so dynamic getters stay fresh.
    const min = resolveTimeBound(minProp);
    const max = resolveTimeBound(maxProp);

    const committedValue = useMemo(() => (value ? value.format(TIME_FORMAT) : ''), [value]);

    useEffect(() => {
      setInputValue(committedValue);
    }, [committedValue]);

    const times = useMemo(() => generateTimes(minuteStep, min, max, value), [minuteStep, min, max, value]);
    const displayTimes = useMemo(
      () => insertSortedUniqueTime(times, value ?? null, min, max),
      [times, value, min, max],
    );

    const selectedIndex = useMemo(
      () => displayTimes.findIndex((time) => time.format(TIME_FORMAT) === committedValue),
      [committedValue, displayTimes],
    );

    const scrollTargetIndex = useMemo(() => {
      if (selectedIndex >= 0) {
        return selectedIndex;
      }

      const anchorDate = getAnchorDate(value, min, max);
      const now = dayjs();
      const nowOnAnchor = anchorDate.hour(now.hour()).minute(now.minute()).second(0).millisecond(0);
      return getClosestTimeIndex(displayTimes, nowOnAnchor);
    }, [displayTimes, max, min, selectedIndex, value]);

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      // Keyboard navigation drives highlightedIndex; scroll nearest to keep it visible.
      // On open (or when no key has moved the highlight yet), center the scroll target.
      const [index, block]: [number | null, ScrollLogicalPosition] =
        highlightedIndex !== null ? [highlightedIndex, 'nearest'] : [scrollTargetIndex, 'center'];

      if (index === null) {
        return;
      }

      const animationFrameId = requestAnimationFrame(() => {
        const option = listRef.current?.querySelector<HTMLElement>(`[data-index="${index}"]`);
        option?.scrollIntoView({ block, behavior: 'auto' });
      });

      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    }, [highlightedIndex, isOpen, scrollTargetIndex]);

    const closePopover = useCallback(() => {
      setIsOpen(false);
      setHighlightedIndex(null);
      setHoveredIndex(null);
      interactionModeRef.current = 'pointer';
    }, []);

    const openPopover = useCallback(() => {
      if (isDisabled) return;
      setIsOpen(true);
      setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : null);
    }, [isDisabled, selectedIndex]);

    const commitInputValue = useCallback(
      (rawValue: string): boolean => {
        const result = normalizeTimeInput({
          rawValue,
          anchorDate: getAnchorDate(value, min, max),
          min,
          max,
        });

        if (result.kind === 'empty') {
          setInputValue('');
          if (committedValue === '') {
            return false;
          }
          onChange?.(null);
          return true;
        }

        if (result.kind === 'invalid') {
          setInputValue(committedValue);
          return false;
        }

        setInputValue(result.text);
        if (result.text === committedValue) {
          return false;
        }
        onChange?.(result.date);
        return true;
      },
      [committedValue, max, min, onChange, setInputValue, value],
    );

    const commitOption = useCallback(
      (next: dayjs.Dayjs) => {
        const text = next.format(TIME_FORMAT);
        setInputValue(text);
        if (text !== committedValue) {
          onChange?.(next);
        }
      },
      [committedValue, onChange, setInputValue],
    );

    const handleInputKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (isDisabled) return;
        const nativeKeyboardEvent = event.nativeEvent as KeyboardEvent;
        if (nativeKeyboardEvent.isComposing) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          setInputValue(committedValue);
          closePopover();
          return;
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          const nextIndex = getNextActiveIndex({
            direction: 'down',
            isOpen,
            currentActiveIndex: highlightedIndex,
            committedValue: value ?? null,
            options: displayTimes,
          });
          if (nextIndex === null) return;
          interactionModeRef.current = 'keyboard';
          setHoveredIndex(null);
          if (!isOpen) {
            setIsOpen(true);
          }
          setHighlightedIndex(nextIndex);
          return;
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();
          const nextIndex = getNextActiveIndex({
            direction: 'up',
            isOpen,
            currentActiveIndex: highlightedIndex,
            committedValue: value ?? null,
            options: displayTimes,
          });
          if (nextIndex === null) return;
          interactionModeRef.current = 'keyboard';
          setHoveredIndex(null);
          if (!isOpen) {
            setIsOpen(true);
          }
          setHighlightedIndex(nextIndex);
          return;
        }

        if (event.key === 'Enter') {
          event.preventDefault();
          if (isOpen && highlightedIndex !== null && displayTimes[highlightedIndex]) {
            commitOption(displayTimes[highlightedIndex]);
            closePopover();
            return;
          }
          commitInputValue(inputValue);
          closePopover();
          return;
        }

        if (event.key === 'Tab') {
          closePopover();
        }
      },
      [
        closePopover,
        commitInputValue,
        commitOption,
        committedValue,
        highlightedIndex,
        inputValue,
        isDisabled,
        isOpen,
        displayTimes,
        setInputValue,
        value,
      ],
    );

    const radius = getRadius('md');
    const paddingX = getSpacing('3');
    const iconGap = getSpacing('2');
    const triggerPaddingX = getSpacing('2');
    const optionPaddingY = getSpacing('2');
    const optionPaddingX = getSpacing('4');
    const height = `${theme.sizes.layout.inputHeight}px`;
    const dropdownMaxHeight = `${theme.sizes.layout.dropdownMaxHeight}px`;
    const dropdownMinWidth = `${theme.sizes.layout.dropdownMinWidth}px`;
    const zIndexPopover = zIndex.popover;
    const font = getFont('M_Regular');

    const borderColor = isInvalid ? getColor('input.border.error') : getColor('input.border');
    const focusBorderColor = getColor('input.border.focus');
    const bgColor = isDisabled ? getColor('input.bg.disabled') : getColor('input.bg');
    const fgColor = isDisabled ? getColor('input.text.disabled') : getColor('input.text');
    const placeholderColor = getColor('input.text.placeholder');

    return (
      <Popover.Root
        open={isOpen}
        lazyMount
        unmountOnExit
        autoFocus={false}
        positioning={{ placement: 'bottom-start', gutter: theme.sizes.layout.popoverGutter, sameWidth: true }}
        onOpenChange={({ open }) => {
          setIsOpen(open);
          if (open) {
            setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : null);
          } else {
            setHighlightedIndex(null);
            setHoveredIndex(null);
            interactionModeRef.current = 'pointer';
          }
        }}
      >
        <Popover.Anchor asChild>
          <div
            css={css`
              position: relative;
              width: 100%;
            `}
          >
            <div
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              aria-controls={isOpen ? listboxId : undefined}
              css={css`
                display: flex;
                align-items: center;
                position: relative;
                width: 100%;
                height: ${height};
                padding: 0 ${paddingX};
                gap: ${iconGap};
                border: ${borderWidth.sm} solid ${borderColor};
                border-radius: ${radius};
                background-color: ${bgColor};
                transition:
                  border-color ${transition.normal},
                  box-shadow ${transition.normal};
                cursor: ${isDisabled ? 'not-allowed' : 'text'};

                &:hover {
                  border-color: ${!isDisabled && !isInvalid ? getColor('input.border.hover') : borderColor};
                }

                &:focus-within {
                  border-color: ${focusBorderColor};
                  box-shadow: 0 0 0 ${borderWidth.sm} ${focusBorderColor};
                }
              `}
              onPointerDown={(event) => {
                if (event.button !== 0 || isDisabled) return;
                if (event.target === inputRef.current) return;
                inputRef.current?.focus({ preventScroll: true });
              }}
            >
              {icon && (
                <div
                  css={css`
                    display: flex;
                    pointer-events: none;
                    flex-shrink: 0;
                  `}
                >
                  <CoreIcon icon={icon} size="sm" color={isDisabled ? 'fg.muted' : 'fg'} />
                </div>
              )}

              <input
                ref={inputRef}
                autoFocus={autoFocus}
                type="text"
                value={inputValue}
                onChange={(event) => {
                  setInputValue(sanitizeTimeDraft(event.currentTarget.value));
                  setHighlightedIndex(null);
                }}
                onFocus={() => {
                  openPopover();
                }}
                onClick={() => {
                  openPopover();
                }}
                onBlur={() => {
                  if (isDisabled) return;
                  // Blur reverts any in-progress typed edit back to the last committed value.
                  // Only Enter and option click commit a new value.
                  setInputValue(committedValue);
                }}
                onKeyDown={handleInputKeyDown}
                disabled={isDisabled}
                aria-invalid={isInvalid || undefined}
                aria-activedescendant={
                  isOpen && highlightedIndex !== null ? `${listboxId}-opt-${highlightedIndex}` : undefined
                }
                placeholder={placeholderText}
                data-testid="core-timeselect-input"
                css={css`
                  position: static;
                  flex: 1;
                  min-width: 0;
                  height: 100%;
                  padding: 0;
                  border: none;
                  outline: none;
                  background: transparent;
                  font-family: ${font.fontFamily};
                  font-size: ${font.size}px;
                  color: ${fgColor};
                  width: 100%;

                  &::placeholder {
                    color: ${placeholderColor};
                  }
                `}
              />

              <button
                disabled={isDisabled}
                type="button"
                tabIndex={-1}
                aria-label={t('common:time.toggleTimePicker')}
                data-testid="core-timeselect-trigger"
                onPointerDown={(event) => {
                  if (isDisabled) return;
                  event.preventDefault();
                  event.stopPropagation();
                  inputRef.current?.focus({ preventScroll: true });
                  if (isOpen) {
                    // Closing via trigger reverts typed edits — only Enter and option click commit.
                    setInputValue(committedValue);
                    closePopover();
                  } else {
                    setIsOpen(true);
                    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : null);
                  }
                }}
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0 ${triggerPaddingX};
                  border: none;
                  background: transparent;
                  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
                  flex-shrink: 0;
                `}
              >
                <CoreIcon icon="chevron_down" size="sm" color="fg.muted" />
              </button>
            </div>
          </div>
        </Popover.Anchor>

        <Portal>
          <Popover.Positioner
            css={css`
              z-index: ${zIndexPopover};
            `}
          >
            <Popover.Content
              data-testid="core-timeselect-popup"
              css={css`
                background-color: ${getColor('bg')};
                border-radius: ${getRadius('md')};
                border: ${borderWidth.sm} solid ${getColor('border')};
                box-shadow: ${shadow.dropdown};
                overflow: hidden;
                max-height: ${dropdownMaxHeight};
                width: var(--reference-width);
                min-width: ${dropdownMinWidth};
              `}
            >
              <div
                ref={listRef}
                id={listboxId}
                role="listbox"
                aria-label={t('common:time.selectTime')}
                onMouseMove={() => {
                  interactionModeRef.current = 'pointer';
                }}
                onMouseLeave={() => {
                  setHoveredIndex(null);
                }}
                css={css`
                  max-height: ${dropdownMaxHeight};
                  overflow-y: auto;
                  padding: ${getSpacing('1')} 0;
                  scrollbar-width: thin;
                  scrollbar-color: ${getColor('card.bg.muted')} transparent;

                  &::-webkit-scrollbar {
                    width: ${getSpacing('1.5')};
                  }
                  &::-webkit-scrollbar-track {
                    background: transparent;
                  }
                  &::-webkit-scrollbar-thumb {
                    background: ${getColor('card.bg.muted')};
                    border-radius: ${getRadius('sm')};
                  }
                `}
              >
                {displayTimes.map((time, index) => {
                  const label = time.format(TIME_FORMAT);
                  const isSelected = committedValue === label;
                  const isHighlighted = highlightedIndex === index;
                  const isHovered = hoveredIndex === index;
                  return (
                    <button
                      key={label}
                      id={`${listboxId}-opt-${index}`}
                      data-index={index}
                      data-value={label}
                      data-highlighted={isHighlighted ? 'true' : undefined}
                      data-hovered={isHovered ? 'true' : undefined}
                      type="button"
                      role="option"
                      tabIndex={-1}
                      aria-selected={isSelected}
                      onPointerDown={(event) => {
                        event.preventDefault();
                      }}
                      onClick={() => {
                        commitOption(time);
                        closePopover();
                      }}
                      onMouseMove={() => {
                        if (interactionModeRef.current !== 'pointer') return;
                        setHoveredIndex(index);
                        setHighlightedIndex(index);
                      }}
                      css={css`
                        display: flex;
                        width: 100%;
                        padding: ${optionPaddingY} ${optionPaddingX};
                        border: none;
                        background: transparent;
                        color: ${getColor('fg')};
                        font-family: ${font.fontFamily};
                        font-size: ${font.size}px;
                        cursor: pointer;
                        text-align: center;
                        justify-content: center;
                        transition: background ${transition.fast};

                        ${isSelected || isHighlighted
                          ? css`
                              background: ${getColor('card.bg.subtle')};
                            `
                          : ''}

                        ${isHovered && !isHighlighted
                          ? css`
                              background-color: ${getColor('card.bg.subtle.hover')};
                            `
                          : ''}
                      `}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
    );
  },
);

TimeSelect.displayName = 'TimeSelect';
